/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Nucleus Admin API
 * Headless, multi-tenant commerce backend for jewelry brands.
 *
 * ## Surfaces
 *
 * | Prefix | Caller | Credential |
 * |---|---|---|
 * | `/platform/v1` | Platform operator | Platform token from **Platform: Auth** |
 * | `/admin/v1` | Staff of one organization | Staff token from **Admin: Auth** |
 * | `/storefront/v1` | A storefront | Store API key from **Admin: API keys** |
 * | `/public/v1` | Anyone (the brand application form) | None; rate limited per IP |
 *
 * Every credential is sent as `Authorization: Bearer <value>`. Use the **Authorize** button once and it applies to every request you try here.
 *
 * ## Conventions
 *
 * - **Ids** are UUID v7 strings. **Timestamps** are ISO 8601 in UTC. **Money** is `{ amount, currency }` with `amount` in integer minor units.
 * - **Lists** paginate by cursor: `?limit=20&cursor=...` in, `{ items, nextCursor }` out, `nextCursor` is `null` on the last page.
 * - **Errors** always use one envelope, `{ error: { code, message, requestId, details?, meta? } }`. Switch on `code`, never on `message`.
 * - **Request ids**: every response carries `x-request-id`. Quote it when reporting a problem.
 * - **Idempotency**: endpoints that create money-related state require an `Idempotency-Key` header; the header is listed on those endpoints.
 * - A record that belongs to another tenant answers **404**, never 403, so existence is not leaked.
 * OpenAPI spec version: 0.1.0
 */
import type {
  GetAdminV1StoresStoreIdGiftCards200,
  GetAdminV1StoresStoreIdGiftCardsIdPathParameters,
  GetAdminV1StoresStoreIdGiftCardsIdTransactions200,
  GetAdminV1StoresStoreIdGiftCardsIdTransactionsPathParameters,
  GetAdminV1StoresStoreIdGiftCardsParams,
  GetAdminV1StoresStoreIdGiftCardsPathParameters,
  GiftCard,
  GiftCardIssued,
  GiftCardSent,
  PatchAdminV1StoresStoreIdGiftCardsIdBody,
  PatchAdminV1StoresStoreIdGiftCardsIdPathParameters,
  PostAdminV1StoresStoreIdGiftCardsBody,
  PostAdminV1StoresStoreIdGiftCardsIdAdjustBody,
  PostAdminV1StoresStoreIdGiftCardsIdAdjustPathParameters,
  PostAdminV1StoresStoreIdGiftCardsIdSendBody,
  PostAdminV1StoresStoreIdGiftCardsIdSendPathParameters,
  PostAdminV1StoresStoreIdGiftCardsPathParameters
} from '../model';

import { customInstance } from '../../../transport/mutator';
import type { BodyType } from '../../../transport/mutator';


type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];


  /**
 * Codes are never returned; search by the last four characters
 * @summary Gift cards of the store, newest first
 */
export const getAdminV1StoresStoreIdGiftCards = (
    { storeId }: GetAdminV1StoresStoreIdGiftCardsPathParameters,
    params?: GetAdminV1StoresStoreIdGiftCardsParams,
 options?: SecondParameter<typeof customInstance<GetAdminV1StoresStoreIdGiftCards200>>,) => {
      return customInstance<GetAdminV1StoresStoreIdGiftCards200>(
      {url: `/admin/v1/stores/${storeId}/gift-cards`, method: 'GET',
        params
    },
      options);
    }
  /**
 * The code is in this response only; Nucleus keeps its hash for lookup and an encrypted copy so staff can email it later. With `sendEmail` the code goes to `recipientEmail` (or the customer) through the gift_card.issued template. Accepts Idempotency-Key: a replay returns the same card and code
 * @summary Issue a gift card as store credit
 */
export const postAdminV1StoresStoreIdGiftCards = (
    { storeId }: PostAdminV1StoresStoreIdGiftCardsPathParameters,
    postAdminV1StoresStoreIdGiftCardsBody: BodyType<PostAdminV1StoresStoreIdGiftCardsBody>,
 options?: SecondParameter<typeof customInstance<GiftCardIssued>>,) => {
      return customInstance<GiftCardIssued>(
      {url: `/admin/v1/stores/${storeId}/gift-cards`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: postAdminV1StoresStoreIdGiftCardsBody
    },
      options);
    }
  /**
 * @summary One gift card
 */
export const getAdminV1StoresStoreIdGiftCardsId = (
    { storeId, id }: GetAdminV1StoresStoreIdGiftCardsIdPathParameters,
 options?: SecondParameter<typeof customInstance<GiftCard>>,) => {
      return customInstance<GiftCard>(
      {url: `/admin/v1/stores/${storeId}/gift-cards/${id}`, method: 'GET'
    },
      options);
    }
  /**
 * `customerId` moves the card to another customer of the store (null detaches it). An expired card's stored expiry may be sent back unchanged
 * @summary Disable, re-enable, extend, annotate or reassign a gift card
 */
export const patchAdminV1StoresStoreIdGiftCardsId = (
    { storeId, id }: PatchAdminV1StoresStoreIdGiftCardsIdPathParameters,
    patchAdminV1StoresStoreIdGiftCardsIdBody: BodyType<PatchAdminV1StoresStoreIdGiftCardsIdBody>,
 options?: SecondParameter<typeof customInstance<GiftCard>>,) => {
      return customInstance<GiftCard>(
      {url: `/admin/v1/stores/${storeId}/gift-cards/${id}`, method: 'PATCH',
      headers: {'Content-Type': 'application/json', },
      data: patchAdminV1StoresStoreIdGiftCardsIdBody
    },
      options);
    }
  /**
 * Writes an `adjust` ledger entry with the note. 422 when the balance would go below zero
 * @summary Correct the balance up or down
 */
export const postAdminV1StoresStoreIdGiftCardsIdAdjust = (
    { storeId, id }: PostAdminV1StoresStoreIdGiftCardsIdAdjustPathParameters,
    postAdminV1StoresStoreIdGiftCardsIdAdjustBody: BodyType<PostAdminV1StoresStoreIdGiftCardsIdAdjustBody>,
 options?: SecondParameter<typeof customInstance<GiftCard>>,) => {
      return customInstance<GiftCard>(
      {url: `/admin/v1/stores/${storeId}/gift-cards/${id}/adjust`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: postAdminV1StoresStoreIdGiftCardsIdAdjustBody
    },
      options);
    }
  /**
 * Queues the gift_card.issued email through the outbox. `to` must be the attached customer's email or the recipient the card was issued for (422 otherwise); it defaults to the customer. 409 for a card whose code was not kept (canSendEmail false) or that is no longer active
 * @summary Email the code to the recipient
 */
export const postAdminV1StoresStoreIdGiftCardsIdSend = (
    { storeId, id }: PostAdminV1StoresStoreIdGiftCardsIdSendPathParameters,
    postAdminV1StoresStoreIdGiftCardsIdSendBody: BodyType<PostAdminV1StoresStoreIdGiftCardsIdSendBody>,
 options?: SecondParameter<typeof customInstance<GiftCardSent>>,) => {
      return customInstance<GiftCardSent>(
      {url: `/admin/v1/stores/${storeId}/gift-cards/${id}/send`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: postAdminV1StoresStoreIdGiftCardsIdSendBody
    },
      options);
    }
  /**
 * @summary Issue, redemptions, refunds and adjustments on the card, newest first
 */
export const getAdminV1StoresStoreIdGiftCardsIdTransactions = (
    { storeId, id }: GetAdminV1StoresStoreIdGiftCardsIdTransactionsPathParameters,
 options?: SecondParameter<typeof customInstance<GetAdminV1StoresStoreIdGiftCardsIdTransactions200>>,) => {
      return customInstance<GetAdminV1StoresStoreIdGiftCardsIdTransactions200>(
      {url: `/admin/v1/stores/${storeId}/gift-cards/${id}/transactions`, method: 'GET'
    },
      options);
    }
  export type GetAdminV1StoresStoreIdGiftCardsResult = NonNullable<Awaited<ReturnType<typeof getAdminV1StoresStoreIdGiftCards>>>
export type PostAdminV1StoresStoreIdGiftCardsResult = NonNullable<Awaited<ReturnType<typeof postAdminV1StoresStoreIdGiftCards>>>
export type GetAdminV1StoresStoreIdGiftCardsIdResult = NonNullable<Awaited<ReturnType<typeof getAdminV1StoresStoreIdGiftCardsId>>>
export type PatchAdminV1StoresStoreIdGiftCardsIdResult = NonNullable<Awaited<ReturnType<typeof patchAdminV1StoresStoreIdGiftCardsId>>>
export type PostAdminV1StoresStoreIdGiftCardsIdAdjustResult = NonNullable<Awaited<ReturnType<typeof postAdminV1StoresStoreIdGiftCardsIdAdjust>>>
export type PostAdminV1StoresStoreIdGiftCardsIdSendResult = NonNullable<Awaited<ReturnType<typeof postAdminV1StoresStoreIdGiftCardsIdSend>>>
export type GetAdminV1StoresStoreIdGiftCardsIdTransactionsResult = NonNullable<Awaited<ReturnType<typeof getAdminV1StoresStoreIdGiftCardsIdTransactions>>>
